import styled from 'styled-components'

export const PageContainer = styled.div`
  height: calc(100vh - var(--header-height, 48px));
  position: fixed;
  top: var(--header-height, 48px);
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  background: #fafafa;
  overflow: hidden;
`

export const MainContainer = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  position: relative;

  @media (max-width: 1024px) {
    /* Ensure content takes full width on mobile */
    flex-direction: column;
  }
`

export const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  min-width: 0;
`

export const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow-y: auto;
  background: white;
  position: relative;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  /* Extra padding on mobile/tablet for FAB button (56px + 20px bottom margin + 16px buffer) */
  @media (max-width: 1200px) {
    padding-bottom: 92px;
  }
`

export const MapWrapper = styled.div<{
  $isOpen?: boolean
  $width?: number
  $hidden?: boolean
}>`
  width: ${(props) => (props.$width ? `${props.$width}px` : '580px')};
  min-width: 320px;
  max-width: 90vw;
  height: 100%;
  background: #f5f5f5;
  border-left: 1px solid #e5e5e5;
  position: relative;
  flex-shrink: 0;
  transition: ${(props) => (props.$width ? 'none' : 'transform 0.3s ease')};

  /* Collapsed (desktop): pane slides to zero width; the map's ResizeObserver repaints it on restore. */
  ${(props) =>
    props.$hidden &&
    `
    width: 0;
    min-width: 0;
    border-left: none;
    overflow: hidden;
    transition: width 0.28s ease;
  `}

  @media (max-width: 1200px) {
    position: fixed;
    top: ${({ theme }) => theme.layout.appHeaderHeight};
    left: 0;
    right: 0;
    bottom: 0;
    height: calc(100dvh - ${({ theme }) => theme.layout.appHeaderHeight});
    width: 100%;
    max-width: 100%;
    border-left: none;
    z-index: 1000;
    transform: translateX(${(props) => (props.$isOpen ? '0' : '100%')});
    transition: transform 0.3s ease;
  }
`

export const MapCloseButton = styled.button`
  display: none;

  @media (max-width: 1200px) {
    display: flex;
    position: absolute;
    top: 12px;
    right: 8px;
    width: 36px;
    height: 36px;
    background: white;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.default};
    box-shadow: ${({ theme }) => theme.shadows.md};
    cursor: pointer;
    align-items: center;
    justify-content: center;
    z-index: 10;
    transition: all ${({ theme }) => theme.transitions.default};

    &:hover {
      background: ${({ theme }) => theme.colors.background.tertiary};
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }

    /* Disable hover effects on touch devices */
    @media (hover: none) {
      &:hover {
        background: white;
        transform: none;
      }

      &:active {
        background: ${({ theme }) => theme.colors.background.tertiary};
        transform: scale(0.95);
      }
    }
  }
`

export const MobileMapToggle = styled.button`
  display: none;
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 56px;
  height: 56px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  border: none;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  cursor: pointer;
  z-index: 999;
  align-items: center;
  justify-content: center;
  transition: all ${({ theme }) => theme.transitions.default};

  @media (max-width: 1200px) {
    display: flex;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      background: ${({ theme }) => theme.colors.primary};
      transform: none;
    }

    &:active {
      background: ${({ theme }) => theme.colors.primaryHover};
      transform: scale(0.95);
    }
  }

  svg {
    width: 24px;
    height: 24px;
  }
`

// Floating "Show map" pill — appears bottom-right when the map pane is hidden (desktop only).
export const MapRestorePill = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 999;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  transition: all ${({ theme }) => theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.xl};
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  /* Below the layout breakpoint the map is a full-screen overlay with its own toggle. */
  @media (max-width: 1200px) {
    display: none;
  }
`
